/**
 * Page Transition Utility
 *
 * - Overlay: Tailwind `animate-in` + horizontal slide (matches desktop two-pane).
 * - Dynamic: CSS keyframe enter classes (see forms.css) — always runs from the
 *   correct starting transform, same idea as overlay, without relying on transition
 *   from arbitrary prior state. Do not fade entering page content in (no opacity 0).
 *
 * Drawer height easing stays in useDrawerHeightTransition + drawer-height-animate;
 * page enter classes still use dynamic branch when formDisplayMode is dynamic.
 */

/**
 * Marks a `.page` as a form step: the card itself, which switches in place with no slide, rise
 * or fade (forms.css `.page.cs-form-step`). The step's CONTENT carries the direction class from
 * getPageTransitionClasses, so the card, its edge and its title strip hold still between steps.
 * Every step shell (FormStartPageShell, FormPageShell) puts this on its page and hands the
 * transition class to its content; a new step shell does the same.
 */
export const FORM_STEP_CLASS = 'cs-form-step';

/**
 * Marks the part of a card that slides when its whole surface (the detail page, or the form)
 * becomes active — the card body below the image or title strip. FormSurfacePage plays one slide
 * on it per switch. It is a different element from a step's own content, so the surface slide
 * and a step's slide never fight over one element's animation.
 */
export const SURFACE_CONTENT_CLASS = 'cs-surface-content';

/** The one-shot enter class FormSurfacePage puts on its page while the switch plays. */
export const getSurfaceEnterClass = (direction, formDisplayMode) => {
  if (!direction || formDisplayMode === 'overlay') return '';
  return direction === 'forward' ? 'cs-surface-enter-forward' : 'cs-surface-enter-back';
};

/**
 * @param {boolean} isActive - Whether the page is currently active
 * @param {'forward'|'back'|null} direction - Navigation direction, null = no animation
 * @param {'overlay'|'dynamic'} formDisplayMode - Display mode
 * @returns {string} Tailwind class string (overlay) or forms.css hook class (dynamic); empty if none
 */
export const getPageTransitionClasses = (isActive, direction, formDisplayMode) => {
  if (!isActive || !direction) return '';
  if (formDisplayMode === 'overlay') {
    return direction === 'forward'
      ? 'animate-in slide-in-from-right-2 duration-[var(--cs-motion-slow)] ease-out'
      : 'animate-in slide-in-from-left-2 duration-[var(--cs-motion-slow)] ease-out';
  }
  if (formDisplayMode === 'dynamic') {
    return direction === 'forward' ? 'dynamic-slide-forward' : 'dynamic-slide-back';
  }
  return '';
};
